/**
 * How this run compares with the one before it.
 *
 * Renders the backend's own `trend`; the two scores are never diffed here to
 * decide a direction.
 *
 * All FOUR trend values are handled, including `incomparable` — which the old
 * note dropped on the floor. The backend sets it when the two runs were scored
 * by different scoring models, and hiding that leaves a reader comparing two
 * numbers that were never comparable. `trend_reason` is already written for a
 * human, so it is shown verbatim.
 *
 * There is no chart here and there must not be one: only the current run and a
 * single predecessor exist per chain. Two points is a delta, not a series.
 */

import { Info, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import Tooltip from '../../ui/Tooltip';
import type { ValidationChain } from '../../../types/dataQuality';
import { formatQualityScore } from '../utils/dataQualityFormat';

export interface TrendPillProps {
  chain: ValidationChain;
  /** Show `unchanged` too. The console does; the compact strip does not. */
  verbose?: boolean;
}

export default function TrendPill({ chain, verbose = false }: TrendPillProps) {
  const previous = chain.previous;
  if (!chain.trend || !previous) return null;

  if (chain.trend === 'incomparable') {
    const reason =
      chain.trend_reason ||
      'the two runs were scored by different models, so their scores are not comparable';
    return (
      <Tooltip content={reason} position="top">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-1 text-xs text-neutral-600">
          <Info className="h-3.5 w-3.5 shrink-0 text-neutral-400" aria-hidden="true" />
          Not comparable with run {previous.run_number}
        </span>
      </Tooltip>
    );
  }

  if (chain.trend === 'unchanged') {
    if (!verbose) return null;
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-1 text-xs text-neutral-600">
        <Minus className="h-3.5 w-3.5 shrink-0 text-neutral-400" aria-hidden="true" />
        Unchanged since the last refresh
      </span>
    );
  }

  const improved = chain.trend === 'improved';
  const Icon = improved ? TrendingUp : TrendingDown;

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${
        improved
          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
          : 'border-amber-200 bg-amber-50 text-amber-800'
      }`}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      {improved ? 'Improved' : 'Declined'} since the last refresh
      <span className="font-mono tabular-nums">
        {formatQualityScore(previous.quality_score)} → {formatQualityScore(chain.summary.quality_score)}
      </span>
    </span>
  );
}
